"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { FakeFs } from "@/lib/fake-fs";
import {
  buildContactHelp,
  parseContactArgs,
  runCommand,
} from "@/components/heroes/terminal/terminal-commands";
import { completeInput } from "@/components/heroes/terminal/terminal-completion";
import { buildPromptHtml, promptFor } from "@/components/heroes/terminal/terminal-prompt";
import {
  buildInitialHistory,
  renderHistoryLine,
  type Line,
  type Prefill,
} from "@/components/heroes/terminal/terminal-render";

/**
 * Tiny shell emulator. Supports `cd`, `pwd`, `ls`, `whoami`, `cat`,
 * `clear`, `help`, `echo`, `date`, `uname`, `hostname`. Executing
 * `./contact.sh` (or `bash contact.sh`) posts to /api/contact.
 *
 * The filesystem is a server-built snapshot passed in via the `fs`
 * prop; it mirrors the real site: `~` is home, and contains `blog/`,
 * `projects/`, `about.md`, `contact.sh`. `cd` outside of `~` is
 * rejected with a permission-denied error.
 *
 * The dispatch (runCommand), Tab completion (completeInput), and
 * history-line rendering (renderHistoryLine) live in the
 * `terminal-*` modules under this directory — this file is just
 * the React component glue: state, refs, effects, and JSX.
 */
export function InteractiveTerminal({
  fs,
  prefill,
  autoCommands,
  children,
}: {
  fs: FakeFs;
  prefill?: Prefill[];
  /** Optional commands to "type" into the input on mount, one
   *  char at a time, then submit. Used by the boot hero to
   *  replay the preset commands (`whoami`, `ls`) through the
   *  normal submit path so they render with the correct prompt
   *  colors and no extra newline. */
  autoCommands?: string[];
  /** Optional content (typically a static boot transcript) to
   *  render at the top of the scrollable history area, above
   *  the prefill lines. Server-rendered children of a client
   *  component are fine in Next.js. */
  children?: React.ReactNode;
}) {
  const initial = useMemo(
    () => (prefill ? buildInitialHistory(prefill) : { history: [], cwd: "~" }),
    [prefill],
  );
  const [history, setHistory] = useState<Line[]>(initial.history);
  const [cwd, setCwd] = useState<string>(initial.cwd);
  const [input, setInput] = useState<string>("");
  const [cmdHistory, setCmdHistory] = useState<string[]>(prefill?.map((p) => p.cmd) ?? []);
  const [cmdHistoryIdx, setCmdHistoryIdx] = useState<number>(-1);
  // Refs for the live input row. `editableRef` is the contenteditable
  // span that holds the user's input. The prompt sits as a
  // separate `contentEditable={false}` sibling — the prompt can't
  // receive a caret (because it's non-editable), and the editable
  // span can't receive a caret before the prompt (because it's a
  // separate element). This dodges the contenteditable "caret before
  // non-editable child" trap that the single-div approach hit.
// `promptRef` is the prompt span; we update its HTML directly when
  // `cwd` changes so React doesn't re-render the prompt subtree
  // (which would clobber any selection inside the editable span).
  const editableRef = useRef<HTMLSpanElement | null>(null);
  const promptRef = useRef<HTMLSpanElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Ref to the latest `submit` function. The autoCommands
  // effect calls `submitRef.current(cmd)` so it always reaches
  // the render that has the latest `cwd` / `history` / `prompt`
  // closure values, even though the effect itself only runs
  // once per `autoCommands` change.
  const submitRef = useRef<(cmd: string) => void>(() => {});

  // The full prompt string (including the trailing space). Cached
  // as a memo so the DOM-write helpers stay in sync — when `cwd`
  // changes, the memo changes, and the effect below updates the
  // prompt span in place. We also derive a colored HTML form of
  // the same prompt for the prompt span's `innerHTML` — this lets
  // the JSX render an empty placeholder (no reactive content
  // inside the contenteditable subtree) while still showing the
  // user the same colors as the rest of the terminal.
  const promptText = useMemo(() => promptFor(cwd), [cwd]);
  const promptHtml = useMemo(() => buildPromptHtml(cwd), [cwd]);

  // Write the editable portion of the contenteditable input to a
  // given string, replacing whatever the user (or a previous
  // write) had. Places the caret at the end of the new text so
  // the user can keep typing. Returns early if the input ref
  // isn't mounted yet.
  //
  // The editable span is uncontrolled — we set its `textContent`
  // directly (no HTML parsing) and move the caret to the end.
  // The baseline-anchor zero-width space lives in a `::before`
  // pseudo-element (see the CSS at the bottom of the component)
  // so we don't have to manage it here.
  function writeEditable(newInput: string) {
    const el = editableRef.current;
    if (!el) return;
    el.textContent = newInput;
    // Place the caret at the end of the editable text. Using
    // a collapsed range that ends at the editable span's last
    // child text node keeps the selection inside the editable
    // region (the prompt span is non-editable and would reject
    // a caret there anyway).
    const range = document.createRange();
    if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
      range.setStart(el.firstChild, newInput.length);
      range.collapse(true);
    } else {
      range.selectNodeContents(el);
      range.collapse(false);
    }
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  // Read the editable portion from the DOM. The editable span
  // only ever contains the user's text (the prompt is a separate
  // sibling; the baseline-anchor zero-width space lives in a
  // `::before` pseudo-element, not in the DOM text), so the
  // whole `textContent` is the command. If somehow the DOM got
  // into a bad state, return an empty string rather than
  // corrupting React state.
  function readEditable(): string {
    const el = editableRef.current;
    if (!el) return "";
    return el.textContent ?? "";
  }

  // Bridge DOM input events into React state. The editable span
  // is uncontrolled — we read the current text from the DOM on
  // every input event and update React state to match. React
  // doesn't re-render the editable text (only the prompt span,
  // on cwd change), so we don't fight the user's cursor.
  function onDomInput() {
    const next = readEditable();
    setInput(next);
  }

  // When the user clicks anywhere in the input row (including
  // the prompt span), focus the editable portion and place the
  // caret at the end of the typed text. This matches how a real
  // terminal behaves: clicking the prompt area doesn't move the
  // caret somewhere weird, it just focuses the command line.
  // We don't run this handler for clicks that land *inside* the
  // editable span itself — letting the browser handle those
  // natively preserves the user's click-position caret.
  function focusEditableAtEnd() {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
      const len = (el.firstChild.textContent ?? "").length;
      range.setStart(el.firstChild, len);
      range.collapse(true);
    } else {
      range.selectNodeContents(el);
      range.collapse(false);
    }
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  // Strip HTML from paste. The default contenteditable paste
  // inserts rich HTML, which would put the prompt subtree at
  // risk (the editable div could end up with extra spans
  // before the prompt span). We intercept the event, read the
  // plain text from the clipboard, and insert it manually at
  // the current caret position via `execCommand('insertText')`
  // — this also routes through the browser's input pipeline so
  // the resulting text lands as a sibling of the prompt span
  // and `onDomInput` fires to sync state.
  function onPastePlain(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;
    // Strip newlines — the shell input is single-line in
    // practice, and an embedded newline would create a new
    // visual line inside the contenteditable and confuse the
    // caret math.
    const clean = text.replace(/\r?\n/g, " ");
    document.execCommand("insertText", false, clean);
  }

  // When `cwd` changes, replace the prompt span's HTML and
  // rewrite the editable div so the editable text stays
  // anchored to the new prompt. We use `innerHTML` on the
  // When `cwd` changes, replace the prompt span's HTML so the
  // colored spans get rebuilt with the new path component. The
  // editable span is left alone — its `textContent` is the
  // user's input and survives cwd changes without intervention.
  useEffect(() => {
    const promptEl = promptRef.current;
    if (promptEl) {
      promptEl.innerHTML = promptHtml;
    }
  }, [promptText, promptHtml]);

  // Auto-replay: if `autoCommands` is provided, type each command
  // into the input one char at a time, then submit. This lets the
  // boot hero replay preset commands through the normal submit
  // path (correct prompt colors, no extra newline) instead of
  // pre-rendering them as static text.
  useEffect(() => {
    if (!autoCommands || autoCommands.length === 0) return;
    let cancelled = false;
    const TYPE_DELAY = 28; // ms per char
    const SUBMIT_GAP = 90; // ms between commands

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        if (cancelled) return resolve();
        setTimeout(resolve, ms);
      });

    const run = async () => {
      for (const cmd of autoCommands) {
        if (cancelled) return;
        // Type one char at a time so the input row updates visibly
        // (same cadence as the boot sequence's typed lines).
        for (let i = 1; i <= cmd.length; i++) {
          if (cancelled) return;
          const partial = cmd.slice(0, i);
          writeEditable(partial);
          setInput(partial);
          await sleep(TYPE_DELAY);
        }
        if (cancelled) return;
        // Submit: clear the input and run the command. We
        // snapshot the input before clearing so `submit` gets
        // the full command string.
        const full = cmd;
        writeEditable("");
        setInput("");
        submitRef.current(full);
        await sleep(SUBMIT_GAP);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [autoCommands]);

  useEffect(() => {
    editableRef.current?.focus();

    // Page-wide focus: clicking anywhere in the main page content
    // (outside an actual link/button/control) should focus the
    // terminal input. We listen on `document` and bail if the
    // click landed on a real interactive element.
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Skip if the click hit a real control.
      if (target.closest("a, button, input, textarea, select, [role='button']")) return;
      // Skip if the user is selecting text — selectionstart is
      // non-zero when the user dragged from somewhere.
      if (window.getSelection()?.toString()) return;
      editableRef.current?.focus();
    };
    document.addEventListener("pointerdown", onDocPointerDown);

    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
    };
  }, []);

  // Chat-UI behavior: when new output is appended, keep the
  // scrollable area pinned to the bottom — but only if the user
  // was already at the bottom. If they've scrolled up to read
  // history, leave their scroll position alone. On initial mount
  // we deliberately do NOT scroll, so the boot transcript + prefill
  // sit at the top of the scrollable area with the input prompt
  // directly below them (the natural reading order).
  const stickToBottom = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      // Within a few pixels of the bottom counts as "at the bottom".
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottom.current = distance < 8;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  });

  const prompt = useMemo(() => promptFor(cwd), [cwd]);

  function runContact(args: string[]) {
    // Args were already validated by the caller (`submit`); the
    // values here are guaranteed present. We do still re-validate
    // defensively so the function is safe to call from anywhere.
    const { parsed, missing } = parseContactArgs(args);
    if (missing.length > 0) {
      setHistory((h) => [
        ...h,
        { kind: "prompt", text: prompt },
        { kind: "cmd", text: input },
        ...buildContactHelp(missing),
      ]);
      writeEditable("");
      setInput("");
      setCmdHistory((h) => [...h, input]);
      setCmdHistoryIdx(-1);
      return;
    }
    setHistory((h) => [
      ...h,
      { kind: "prompt", text: prompt },
      { kind: "cmd", text: input },
      { kind: "out", text: "[contact.sh] sending..." },
    ]);
    writeEditable("");
    setInput("");
    setCmdHistory((h) => [...h, input]);
    setCmdHistoryIdx(-1);

    (async () => {
      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            name: parsed.name,
            email: parsed.email,
            subject: parsed.subject,
            message: parsed.message,
          }),
        });
        const json = (await res.json()) as { ok?: boolean; error?: string };
        const followup =
          res.ok && json.ok
            ? ["[contact.sh] sent."]
            : [`[contact.sh] failed: ${json.error ?? res.statusText}`];
        setHistory((h) => [
          ...h,
          ...followup.map((t) => ({ kind: "out" as const, text: t })),
        ]);
      } catch (e) {
        setHistory((h) => [
          ...h,
          { kind: "err", text: `[contact.sh] network error: ${(e as Error).message}` },
        ]);
      }
    })();
  }

  function submit(cmd: string) {
    const trimmed = cmd.trim();

    // Special-case: contact.sh dispatch. Validate up front so we can
    // show the help block (with clickable socials) before any network
    // attempt.
    if (trimmed.startsWith("./contact.sh") || trimmed.startsWith("bash contact.sh")) {
      const args = (
        trimmed.startsWith("./")
          ? trimmed.slice(2).trim()
          : trimmed.slice(4).trim()
      ).split(/\s+/);
      // First token is the script name; drop it.
      args.shift();
      const { parsed, missing, unknownFlags } = parseContactArgs(args);
      if (missing.length > 0 || unknownFlags.length > 0) {
        setHistory((h) => [
          ...h,
          { kind: "prompt", text: prompt },
          { kind: "cmd", text: cmd },
          ...buildContactHelp(missing, unknownFlags),
        ]);
        writeEditable("");
        setInput("");
        setCmdHistory((h) => [...h, cmd]);
        setCmdHistoryIdx(-1);
        return;
      }
      // Light client-side shape check before firing the request, so
      // obvious bad inputs don't burn a round trip.
      if (!parsed.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsed.email)) {
        setHistory((h) => [
          ...h,
          { kind: "prompt", text: prompt },
          { kind: "cmd", text: cmd },
          { kind: "err", text: `contact.sh: --email looks invalid: ${parsed.email}` },
        ]);
        writeEditable("");
        setInput("");
        setCmdHistory((h) => [...h, cmd]);
        setCmdHistoryIdx(-1);
        return;
      }
      if (!parsed.name?.trim() || !parsed.message?.trim()) {
        setHistory((h) => [
          ...h,
          { kind: "prompt", text: prompt },
          { kind: "cmd", text: cmd },
          { kind: "err", text: "contact.sh: --name and --message must not be empty" },
        ]);
        writeEditable("");
        setInput("");
        setCmdHistory((h) => [...h, cmd]);
        setCmdHistoryIdx(-1);
        return;
      }
      runContact(args);
      return;
    }
    // Use functional state updates so multiple submits in
    // sequence (e.g. the autoCommands replay) each see the
    // latest history + cwd without stale-closure bugs.
    const result = runCommand(cmd, cwd, fs);
    if (result.clear) {
      setHistory([]);
      if (result.newCwd) setCwd(result.newCwd);
      writeEditable("");
      setInput("");
      setCmdHistory((h) => [...h, cmd]);
      setCmdHistoryIdx(-1);
      return;
    }
    setHistory((h) => [
      ...h,
      { kind: "prompt", text: prompt },
      { kind: "cmd", text: cmd },
      ...(result.out ?? []).map((t) => ({ kind: "out" as const, text: t })),
      ...(result.err ?? []).map((t) => ({ kind: "err" as const, text: t })),
    ]);
    if (result.newCwd) setCwd(result.newCwd);
    setCmdHistory((h) => [...h, cmd]);
    setCmdHistoryIdx(-1);
  }

  // Keep the ref pointed at the latest submit so the autoCommands
  // effect always reaches the render with the freshest closures.
  submitRef.current = submit;

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      // Read from DOM, not React state — if the user typed
      // faster than React could commit the onInput-driven state
      // update, the state would lag behind the DOM and we'd
      // submit a stale command.
      const cmd = readEditable();
      writeEditable("");
      setInput("");
      submit(cmd);
      return;
    }
    if (e.key === "Tab") {
      // Tab completion. We compute the caret offset within the
      // current input line, run `completeInput`, and either
      // substitute the completed text or print the available
      // options (like `ls` does for an empty directory).
      e.preventDefault();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      // The editable span is the only contenteditable region
      // inside the input row, so the caret offset within it is
      // just the cursor position relative to its start.
      const editableEl = editableRef.current;
      if (!editableEl) return;
      const preRange = document.createRange();
      preRange.setStart(editableEl, 0);
      preRange.setEnd(range.endContainer, range.endOffset);
      const caretOffset = Math.max(0, preRange.toString().length);
      const result = completeInput(input, caretOffset, cwd, fs);
      if (!result) return;
      if (result.complete !== input) {
        writeEditable(result.complete);
        setInput(result.complete);
        return;
      }
      // Multiple matches with nothing more to add — print the
      // options like `ls` would. We add them as `out` lines
      // under the current prompt so the user can see what's
      // available, then leave the input unchanged.
      if (result.options.length > 0) {
        setHistory((h) => [
          ...h,
          { kind: "prompt", text: prompt },
          { kind: "cmd", text: input },
          ...result.options.map((o) => ({ kind: "out" as const, text: o })),
        ]);
      }
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const next = cmdHistoryIdx < 0 ? cmdHistory.length - 1 : Math.max(0, cmdHistoryIdx - 1);
      setCmdHistoryIdx(next);
      writeEditable(cmdHistory[next]);
      setInput(cmdHistory[next]);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (cmdHistory.length === 0 || cmdHistoryIdx < 0) return;
      const next = cmdHistoryIdx + 1;
      if (next >= cmdHistory.length) {
        setCmdHistoryIdx(-1);
        writeEditable("");
        setInput("");
      } else {
        setCmdHistoryIdx(next);
        writeEditable(cmdHistory[next]);
        setInput(cmdHistory[next]);
      }
    }
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 font-mono text-[0.7rem] sm:text-xs leading-tight">
      {/* Scrollable history: server-rendered header (boot transcript)
          above, then the live history lines. Clicking anywhere in
          this area focuses the input. */}
      <div
        ref={scrollRef}
        onClick={() => editableRef.current?.focus()}
        className="flex-1 min-h-0 overflow-y-auto cursor-text"
      >
        <pre className="whitespace-pre-wrap break-words">
          {children}
          {history.map((l, i) => renderHistoryLine(l, i))}

      {/* Live input row — sits at the end of the scrollable content,
          not pinned to the viewport. This matches how a real terminal
          works: the prompt is just the next line in the scrollback,
          and the auto-scroll-to-bottom effect keeps it visible as
          new output is appended above it.

          The row is a flex container with two inline children: the
          prompt prefix (non-editable, so the user can never delete
          or modify it) and the editable span (where the user
          actually types). Splitting the editable region into its
          own element sidesteps the contenteditable "caret before
          a non-editable sibling" trap that a single-div layout
          hits: the editable span can only ever hold a caret
          inside itself, and the prompt can never receive one.

          The editable span takes all the leftover width
          (`flex-1 min-w-0`) and line-wraps normally when its
          content overflows. Wrapped lines start at the left edge
          of the row, just like a real terminal where the prompt
          sits on the first line and continuations drop to the
          next column. The editable span is uncontrolled:
          `onInput` reads `textContent` and syncs React state.
          Programmatic updates (Tab completion, history recall,
          autoCommands typing) write into the DOM directly via
          `writeEditable` and restore the caret to the end. React
          never renders content inside the editable span, so the
          user's caret position is preserved across re-renders. */}
      <div className="font-mono outline-none break-words whitespace-pre-wrap cursor-text flex items-baseline">
        {/* The prompt prefix is non-editable and sits at the
            start of the row. Its HTML is rewritten by the
            cwd-update effect (so React doesn't have to
            re-render the prompt subtree, which would clobber
            the user's caret). `select-text` lets the user copy
            the prompt with the mouse; clicking it routes
            through `focusEditableAtEnd` so the caret lands in
            the editable span (not nowhere). We attach the
            handler directly to the prompt span so clicks
            inside the editable span aren't intercepted. */}
        <span
          ref={promptRef}
          contentEditable={false}
          onClick={focusEditableAtEnd}
          className="select-text"
        />
        {/* The editable span takes the rest of the row. We
            `flex-1` so it grows to fill the row, `min-w-0`
            so flex shrink works correctly, and `outline-none`
            so the focus ring doesn't fight the caret color.

            A `::before` pseudo-element with a zero-width space
            keeps the span's baseline well-defined when the
            input is empty — without it, an empty contenteditable
            span doesn't contribute a baseline under
            `items-baseline` flex, which makes the row render
            with the prompt sitting slightly above where it
            does once the user has typed anything. The
            pseudo-element is rendered (so it gives us a
            baseline) but it's not part of the DOM text (so
            `readEditable()` never sees it, `writeEditable()`
            never has to manage it, and the user can't
            accidentally delete it via Backspace). The CSS
            lives in the `<style>` block at the bottom of
            this component. */}
        <span
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          onInput={onDomInput}
          onKeyDown={onKeyDown}
          onPaste={onPastePlain}
          spellCheck={false}
          aria-label="shell input"
          className="flex-1 min-w-0 outline-none terminal-input-editable"
        />
      </div>
        </pre>
      </div>
    </div>
  );
}
